import { useMutation } from 'react-query';
import {
  ApiOptimizationDataPoint,
  IntegrationService,
  OptimizationConstraint,
} from './queries';
import request from './request';

export type ContactFormData = {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone?: string;
  message: string;
  interest: string;
};

export type CreateOptimizationRequest = {
  customer: string;
  optimizationName: string;
  modelBuild: string;
  modelId: string;
  targetInvestment: number;
  period: {
    from: string;
    to: string;
  };
  constraints: Array<OptimizationConstraint>;
};

type CreateOptimizationResponse = { data: ApiOptimizationDataPoint };

export type DeleteOptimizationRequest = {
  customer: string;
  optimizationId: string;
};

// Backend returns empty object but mutation returns optimizationId
export type DeleteOptimizationResponse = {
  optimizationId: string;
};

export function useCreateOptimizationMutation() {
  return useMutation((data: CreateOptimizationRequest) => {
    const { customer, ...bodyData } = data;

    return request
      .post<CreateOptimizationResponse>('/data/optimizations', bodyData, {
        params: {
          customer,
        },
      })
      .then((response) => {
        return response.data.data;
      });
  });
}

export function useDeleteOptimizationMutation() {
  return useMutation(
    ({ customer, optimizationId }: DeleteOptimizationRequest) => {
      return request
        .delete<DeleteOptimizationResponse>('/data/optimizations', {
          params: {
            customer,
            optimization_id: optimizationId,
          },
        })
        .then(() => {
          return { optimizationId };
        });
    },
  );
}

export function useUpdateCustomerMutation() {
  return useMutation((data: { id: string; isRegistrationStarted: boolean }) => {
    return request.post(
      '/data/customers/registration-status',
      {
        isRegistrationStarted: data.isRegistrationStarted,
      },
      {
        params: {
          customer: data.id,
        },
      },
    );
  });
}

export type ContactFormSubmission = {
  formData: ContactFormData;
  captchaToken: string;
  honeypot: string;
};

export function useContactFormMutation() {
  return useMutation((submission: ContactFormSubmission) => {
    return request
      .post('/public/contact/send-email', submission)
      .then((response) => {
        return response.data;
      });
  });
}

export type CareerApplicationSubmission = {
  jobId: string;
  formData: Record<string, string>;
  captchaToken: string;
  honeypot: string;
};

export function useCareerApplicationMutation() {
  return useMutation((submission: CareerApplicationSubmission) => {
    return request
      .post('/public/careers/apply', submission)
      .then((response) => {
        return response.data;
      });
  });
}

export type DisconnectDataConnectorRequest = {
  customer: string;
  service: IntegrationService;
};

export function useDisconnectDataConnectorMutation() {
  return useMutation(
    ({ customer, service }: DisconnectDataConnectorRequest) => {
      return request
        .delete('/data/data-connectors', {
          params: {
            customer,
            service,
          },
        })
        .then((response) => {
          return response.data;
        });
    },
  );
}

export type UpdateSelectedAccountsRequest = {
  customer: string;
  service: IntegrationService;
  selectedAccountIds: string[];
};

export function useUpdateSelectedAccountsMutation() {
  return useMutation(
    ({
      customer,
      service,
      selectedAccountIds,
    }: UpdateSelectedAccountsRequest) => {
      return request
        .put(
          '/data/data-connectors/accounts',
          { service, selectedAccountIds },
          {
            params: {
              customer,
            },
          },
        )
        .then((response) => {
          return response.data;
        });
    },
  );
}

export type UpdateCampaignFilterRequest = {
  customer: string;
  service: IntegrationService;
  campaignNameFilter: string | null;
};

export function useUpdateCampaignFilterMutation() {
  return useMutation(
    ({
      customer,
      service,
      campaignNameFilter,
    }: UpdateCampaignFilterRequest) => {
      return request
        .put(
          '/data/data-connectors/campaign-filter',
          { service, campaignNameFilter },
          {
            params: {
              customer,
            },
          },
        )
        .then((response) => {
          return response.data;
        });
    },
  );
}

export type CreateForecastRequest = {
  customer: string;
  forecastName: string;
  modelBuild: string;
  modelId: string;
  periods: number;
  frequency: 'weekly' | 'monthly';
  scenarios: Array<{
    scenarioName: string;
    mediaSpend: Record<string, number>;
  }>;
};

export type DeleteForecastRequest = {
  customer: string;
  forecastId: string;
};

export type CreateForecastPayload = Omit<
  CreateForecastRequest,
  'customer' | 'modelBuild' | 'modelId'
>;

export function useCreateForecastMutation() {
  return useMutation((data: CreateForecastRequest) => {
    const { customer, ...bodyData } = data;
    return request
      .post('/data/forecasts', bodyData, {
        params: { customer },
      })
      .then((response) => {
        return response.data.data;
      });
  });
}

export function useDeleteForecastMutation() {
  return useMutation(({ customer, forecastId }: DeleteForecastRequest) => {
    return request
      .delete('/data/forecasts', {
        params: {
          customer,
          forecast_id: forecastId,
        },
      })
      .then(() => {
        return { forecastId };
      });
  });
}
